/**
 * VenuePublicPage = MicrositeRouter. Dispatches on `microsite_template`:
 * 'action_first' → ActionFirstMicrosite; 'editorial', unset, or unknown →
 * EditorialMicrosite (the default surface doubles as the safe fallback).
 * This component owns the venue + events fetch and passes merged data to
 * the chosen template.
 */
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Skeleton, Alert, Button, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Suspense, lazy, useMemo } from 'react';
import httpClient from '../../utils/httpClient';
import { usePageMeta } from '../../hooks/usePageMeta';
import { colors } from '../../theme/theme';
import { queryKeys } from '../../utils/queryKeys';
import { useTranslation } from 'react-i18next';
import type { EditorialEvent, EditorialVenue } from '../../components/microsite/Editorial/types';

// Editorial bundle is code-split so the app shell doesn't pay for the
// cream/serif assets until a microsite renders. The Suspense fallback
// renders an h1 with the venue name so a11y crawlers + tests (which
// waitFor an h1) see meaningful content during chunk load.
const EditorialMicrosite = lazy(
  () => import('../../components/microsite/Editorial/EditorialMicrosite'),
);

// Action-First template (dark, conversion-optimised) — same lazy pattern.
const ActionFirstMicrosite = lazy(
  () => import('../../components/microsite/ActionFirst/ActionFirstMicrosite'),
);

/**
 * Chunk-loading fallback: immediate h1 (venue name) for screen readers +
 * tests, and palette-matched skeletons ('editorial' cream vs
 * 'action_first' dark) so the wrong color never flashes mid-load.
 */
function MicrositeChunkFallback({
  name,
  palette = 'editorial',
}: {
  name: string;
  palette?: 'editorial' | 'action_first';
}) {
  const tone =
    palette === 'editorial'
      ? { bg: '#FAF7F2', skel: '#F0EAE0' }
      : { bg: '#0F0F13', skel: 'rgba(255,255,255,0.04)' };
  return (
    <Box sx={{ background: tone.bg, minHeight: '100vh' }}>
      <Box sx={{ position: 'absolute', clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)' }}>
        <Typography component="h1" sx={{ fontSize: 1 }}>{name}</Typography>
      </Box>
      <Skeleton variant="rectangular" height={400} sx={{ bgcolor: tone.skel }} animation="wave" />
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Skeleton variant="text" height={60} width="60%" sx={{ bgcolor: tone.skel }} />
        <Skeleton variant="text" height={20} width="40%" sx={{ bgcolor: tone.skel, mb: 4 }} />
        <Skeleton variant="rectangular" height={200} sx={{ bgcolor: tone.skel, borderRadius: 1 }} />
      </Container>
    </Box>
  );
}

export default function VenuePublicPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?preview=editorial|action_first lets the partner editor + QA preview
  // either template without writing to the DB; absent → persisted field.
  const previewOverride = searchParams.get('preview');

  // Venue fetch (shared between legacy and Editorial templates)
  const { data: venue, isLoading: venueLoading, error: venueError } = useQuery<any>({
    queryKey: queryKeys.venues.publicDetail(slug || ''),
    queryFn: async ({ signal }) =>
      (await httpClient.get(`/venues/${slug}`, { signal })).data,
    enabled: !!slug,
  });

  // Events fetch — used by Editorial.Events. Failure must not hide the venue.
  const { data: events = [] } = useQuery<EditorialEvent[]>({
    queryKey: queryKeys.venues.publicEvents(slug || ''),
    queryFn: async ({ signal }) =>
      (await httpClient.get(`/venues/${slug}/events`, { signal })).data,
    enabled: !!slug,
  });

  // SEO meta: microsite_meta_description → description → generic.
  usePageMeta({
    title: venue ? `${venue.name} — CoffeeMates` : undefined,
    description:
      venue?.microsite_meta_description ||
      venue?.description ||
      (venue ? `Visit ${venue.name} on CoffeeMates` : undefined),
    image: venue?.og_image_url || venue?.cover_photo_url || venue?.photo_url,
    url: venue?.slug ? `https://coffeemates.biz/venues/${venue.slug}` : undefined,
  });

  // Compose the EditorialVenue prop ONCE, memoized — fewer re-renders down
  // through 8 child sections.
  const editorialVenue: EditorialVenue | null = useMemo(() => {
    if (!venue) return null;
    return { ...venue, events };
  }, [venue, events]);

  // ── Loading state ──
  if (venueLoading) {
    return (
      <Box sx={{ bgcolor: colors.background.default, minHeight: '100vh', pb: 8 }}>
        <Skeleton variant="rectangular" height={320} sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} />
        <Container maxWidth="md" sx={{ mt: -4, position: 'relative', zIndex: 1 }}>
          <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 4, bgcolor: 'rgba(255,255,255,0.04)', mb: 3 }} />
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 4, bgcolor: 'rgba(255,255,255,0.04)' }} />
        </Container>
      </Box>
    );
  }

  // ── Error state ──
  if (venueError || !venue) {
    return (
      <Box sx={{ bgcolor: colors.background.default, minHeight: '100vh', pt: 4 }}>
        <Container maxWidth="sm">
          <Alert severity="error" sx={{ mb: 2 }}>
            {(venueError as Error)?.message || t('venue.not_found', 'Venue not found.')}
          </Alert>
          <Button variant="outlined" onClick={() => navigate('/')}>
            ← Browse all venues
          </Button>
        </Container>
      </Box>
    );
  }

  // ── Dispatch on template field ──
  // Unset/empty/unknown → 'editorial' (matches backend venue_detail()
  // fallback). ?preview= wins over the persisted field for QA/editor
  // preview. The pre-Z-track legacy page (and its VITE_MICROSITE_LEGACY_
  // FALLBACK rollback flag) was deleted 2026-09-05 (I2) — it hardcoded a
  // Google Maps API key into the shipped bundle.
  const persistedTemplate =
    (venue.microsite_template as string | undefined) || 'editorial';
  const template =
    previewOverride === 'editorial' || previewOverride === 'action_first'
      ? previewOverride
      : persistedTemplate;

  if (template === 'action_first') {
    return (
      <Suspense fallback={<MicrositeChunkFallback name={venue.name} palette="action_first" />}>
        <ActionFirstMicrosite venue={editorialVenue!} />
      </Suspense>
    );
  }

  // 'editorial' and any unknown template value both land here — editorial
  // is the default surface and the safe fallback.
  return (
    <Suspense fallback={<MicrositeChunkFallback name={venue.name} palette="editorial" />}>
      <EditorialMicrosite venue={editorialVenue!} />
    </Suspense>
  );
}
